import { formatInTimeZone, toDate } from 'date-fns-tz'

type DateInput = Date | string | null | undefined

/**
 * A payload timestamp as the instant it names.
 *
 * Exported because a screen that *prints* these values through the formatters below and still
 * *compares* them with a native `new Date(...)` is reading one string two ways: no-offset strings
 * are UTC, and the native parse takes them as the viewer's own clock. The times on screen and the
 * gates behind them have to come from the same parse. Found by review on #1936.
 */
export const toUtcDate = (value: DateInput): Date | null => {
  if (value == null || value === '') return null

  const utc = toDate(value, { timeZone: 'UTC' })
  return Number.isNaN(utc.getTime()) ? null : utc
}

export const formatDateByUserTimeZone = (releaseTimestamp: DateInput, pattern: string = 'MMMM d, yyyy hh:mm a'): string => {
  const utc = toUtcDate(releaseTimestamp)
  if (utc == null) return ''

  const userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
  const output = formatInTimeZone(utc, userTimeZone, pattern)
  return output
}

/**
 * The same value, said to be the viewer's own clock.
 *
 * For screens where a Backend sentence can name the same instant in UTC, so the two readings
 * are not taken as two different times. The instructor dashboard card has no such sentence and
 * prints the plain local date.
 */
export const describeInUserTimeZone = (value: DateInput, pattern?: string): string => {
  const formatted = formatDateByUserTimeZone(value, pattern)
  return formatted === '' ? '' : `${formatted} (your time)`
}

export const formatTermDateRangeByUserTimeZone = (
  start: DateInput,
  end: DateInput
): string => {
  // Use the viewer’s local IANA timezone (e.g., "America/New_York") so the displayed range matches what they expect.
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const startUtc = toUtcDate(start)
  const endUtc = toUtcDate(end)

  if (startUtc == null || endUtc == null) return ''

  const startYear = formatInTimeZone(startUtc, userTimeZone, 'yyyy')
  const endYear = formatInTimeZone(endUtc, userTimeZone, 'yyyy')

  let startStr: string
  let endStr: string

  if (startYear === endYear) {
    // Same year: "Jan 1–Mar 6, 2026"
    startStr = formatInTimeZone(startUtc, userTimeZone, 'MMM d')
    endStr = formatInTimeZone(endUtc, userTimeZone, 'MMM d, yyyy')
  } else {
    // Cross-year: "Dec 31, 2025–Mar 6, 2026"
    startStr = formatInTimeZone(startUtc, userTimeZone, 'MMM d, yyyy')
    endStr = formatInTimeZone(endUtc, userTimeZone, 'MMM d, yyyy')
  }

  // Use en dash (–) for range
  // Example output: "Jan 1 – Mar 6, 2026" or "Dec 31, 2025 – Mar 6, 2026" depending on input years
  // Returns a formatted date range string in the user's local timezone
  return `${startStr} – ${endStr}`
}
